import { findField } from '../format/elements';
import type { FieldElement, FormDefinition } from '../format/form-definition';
import { isReference, type Bound, type Condition, type Validation } from '../format/pieces';
import { sayBound, valueAsBound, type ResolvedRangeValidation, type ResolvedBound } from './range';

export interface ValidationContext {
  holds(condition: Condition): boolean;
  fieldValue(fieldId: string): unknown;
  today: string;
}

type WithoutWhen<V> = V extends unknown ? Omit<V, 'when'> : never;

export type ResolvedValidation = Exclude<WithoutWhen<Validation>, { kind: 'range' }> | ResolvedRangeValidation;

export const resolveValidations = (
  definition: FormDefinition,
  field: FieldElement,
  context: ValidationContext,
): ResolvedValidation[] => {
  return (field.validations ?? []).flatMap(({ when, ...validation }): ResolvedValidation[] => {
    if (when && !context.holds(when)) return [];
    if (validation.kind !== 'range') return [validation];
    const [min, max] = [validation.min, validation.max].map(bound => resolveBound(definition, bound, context));
    return [{ ...validation, min, max }];
  });
};

const resolveBound = (
  definition: FormDefinition,
  bound: Bound | undefined,
  context: ValidationContext,
): ResolvedBound | undefined => {
  if (bound === undefined) return undefined;
  const said = sayBound(definition, bound);
  if (!isReference(bound)) return { kind: 'fixed', value: bound, ...said };
  if (bound.kind === 'today') return { kind: 'today', value: context.today, ...said };
  const field = findField(definition, bound.fieldId);
  if (!field) return undefined;
  const value = valueAsBound(field, context.fieldValue(bound.fieldId));
  return value === undefined ? undefined : { kind: 'fieldValue', value, ...said };
};
